import axios from 'axios';
import config from '../config';

// The backend runs the heavy query -> xlsx -> zip -> upload in an async worker
// (to clear API Gateway's 29s ceiling), so the client POSTs to start a job and
// then polls until the presigned URL is ready. Poll for up to 15 minutes — the
// worker's own timeout — rather than surfacing a generic "Network Error".
const POLL_INTERVAL_MS = 3 * 1000;
const POLL_TIMEOUT_MS = 15 * 60 * 1000;
const REQUEST_TIMEOUT_MS = 30 * 1000;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const request = ({
  url, method, data, params, iotDownloadApiKey,
}) => axios({
  url,
  method,
  data,
  params,
  timeout: REQUEST_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
    'X-API-KEY': iotDownloadApiKey,
  },
  validateStatus: () => true, // inspect the body ourselves below
});

export const downloadChapea = async ({ startDate, endDate, weekLabel }) => {
  const { iotDownloadUrl, iotDownloadApiKey } = config || {};
  const startTime = new Date(`${startDate}T00:00:00.000Z`).toISOString();
  const endTime = new Date(`${endDate}T23:59:59.999Z`).toISOString();
  try {
    const started = await request({
      url: iotDownloadUrl,
      method: 'POST',
      iotDownloadApiKey,
      data: {
        datetime_range: { start_time: startTime, end_time: endTime },
        week_label: weekLabel,
      },
    });
    const { job_id: jobId, error: startError } = started.data || {};
    if (!jobId) {
      return { error: startError || 'Could not start the download. Please try again.' };
    }

    const deadline = Date.now() + POLL_TIMEOUT_MS;
    while (Date.now() < deadline) {
      // Polling is inherently sequential: each GET depends on the prior delay.
      // eslint-disable-next-line no-await-in-loop
      await delay(POLL_INTERVAL_MS);
      // eslint-disable-next-line no-await-in-loop
      const polled = await request({
        url: iotDownloadUrl,
        method: 'GET',
        iotDownloadApiKey,
        params: { job_id: jobId },
      });
      const { status, download_url: downloadUrl, error } = polled.data || {};
      if (status === 'COMPLETED' && downloadUrl) {
        return polled.data;
      }
      if (status === 'FAILED') {
        return { error: error || 'The download could not be produced for the selected range.' };
      }
    }
    return { error: 'The download is taking longer than expected. Try a shorter date range.' };
  } catch (e) {
    if (e.code === 'ECONNABORTED') {
      return { error: 'The download timed out. Try a shorter date range.' };
    }
    return { error: 'Could not reach the download service. Check your connection and try again.' };
  }
};

export default { downloadChapea };
